import * as Sentry from '@sentry/browser'
import axios from 'axios'
import { queryClient } from 'utils/queryClient'

import { getAuthClient, getBetterAuthConfig } from './client'
import { AuthProvider, selectAuthProvider } from './selection'

export type PortalAuthUser = {
  sub?: string
  email?: string
  name?: string
  given_name?: string
  family_name?: string
  picture?: string
  email_verified?: boolean
}

export type AuthStatus = 'unknown' | AuthProvider | 'signed-out'
type SessionState = {
  status: AuthStatus
  isLoading: boolean
  user?: PortalAuthUser
  token?: string
  expiresAt?: number
  supportsAuth0AccountManagement?: boolean
  error?: Error
}
type Action =
  | { type: 'select'; status: AuthStatus }
  | { type: 'session'; session: Omit<SessionState, 'status' | 'isLoading'> }
  | { type: 'clear'; error?: Error }
  | { type: 'error'; error?: Error }

function reduce(state: SessionState, action: Action): SessionState {
  switch (action.type) {
    case 'select':
      return {
        status: action.status,
        isLoading:
          action.status === 'unknown' || action.status === 'better-auth',
      }
    case 'session':
      return { status: 'better-auth', isLoading: false, ...action.session }
    case 'clear':
      return { status: state.status, isLoading: false, error: action.error }
    case 'error':
      return { ...state, error: action.error }
  }
}

export const asError = (error: unknown) =>
  error instanceof Error
    ? error
    : new Error('Could not reach the authentication service.')

/** JWT metadata is used for refresh timing and UI only; the server verifies it. */
function tokenMetadata(token: string) {
  const payload = token.split('.')[1]
  if (!payload)
    throw new Error('The authentication service returned an invalid token.')
  const metadata = JSON.parse(
    atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
  ) as {
    exp?: unknown
    accounts?: { providerId?: string; accountId?: string }[]
  }
  if (
    typeof metadata.exp !== 'number' ||
    !Number.isFinite(metadata.exp) ||
    metadata.exp * 1000 <= Date.now()
  ) {
    throw new Error('Your session has expired. Please log in again.')
  }
  return metadata
}

function clearCustomerState() {
  queryClient.clear()
  Sentry.setUser(null)
}

/** One synchronous state source for React and token consumers, scoped to this provider.
 * A request's identity prevents old responses from reviving a switched or expired session.
 */
export function createSessionStore(status: AuthStatus) {
  let state = reduce({ status, isLoading: false }, { type: 'select', status })
  const listeners = new Set<() => void>()
  let pending: Promise<boolean> | undefined
  const dispatch = (action: Action) => {
    state = reduce(state, action)
    listeners.forEach((listener) => listener())
  }
  const clear = (error?: Error) => {
    pending = undefined
    clearCustomerState()
    dispatch({ type: 'clear', error })
  }
  const select = (next: AuthProvider | 'signed-out') => {
    pending = undefined
    clearCustomerState()
    selectAuthProvider(next)
    dispatch({ type: 'select', status: next })
  }
  const refresh = (): Promise<boolean> => {
    if (state.status !== 'better-auth') return Promise.resolve(false)
    if (pending) return pending
    const operation = Promise.resolve().then(async () => {
      try {
        const { data, error } = await getAuthClient().getSession()
        if (pending !== operation) return false
        if (error)
          throw new Error(error.message || 'Could not read your session.')
        if (
          !data?.user ||
          new Date(data.session.expiresAt).getTime() <= Date.now()
        ) {
          clear()
          return false
        }
        const { baseUrl, providerId } = getBetterAuthConfig()
        const response = await axios.get<{ token: string }>(
          `${baseUrl}/token`,
          {
            withCredentials: true,
            timeout: 30000,
          },
        )
        if (pending !== operation) return false
        const metadata = tokenMetadata(response.data.token)
        if (state.user && state.user.sub !== data.user.id) clearCustomerState()
        selectAuthProvider('better-auth')
        dispatch({
          type: 'session',
          session: {
            user: {
              sub: data.user.id,
              email: data.user.email,
              email_verified: data.user.emailVerified,
              name: data.user.name,
              picture: data.user.image || undefined,
            },
            token: response.data.token,
            expiresAt: (metadata.exp as number) * 1000,
            supportsAuth0AccountManagement:
              Boolean(providerId) &&
              Array.isArray(metadata.accounts) &&
              metadata.accounts.some(
                (account) =>
                  account.providerId === providerId &&
                  Boolean(account.accountId),
              ),
          },
        })
        return true
      } catch (error) {
        if (pending !== operation) return false
        clear(asError(error))
        throw asError(error)
      } finally {
        if (pending === operation) pending = undefined
      }
    })
    pending = operation
    return operation
  }
  return {
    getSnapshot: () => state,
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    select,
    refresh,
    setError: (error?: Error) => dispatch({ type: 'error', error }),
    cancel: () => {
      pending = undefined
    },
    expire: () => {
      if (state.expiresAt && state.expiresAt <= Date.now()) {
        clear(new Error('Your session has expired. Please log in again.'))
      }
    },
    getToken: async () => {
      if (state.status !== 'better-auth')
        throw new Error('Please log in again.')
      if (state.token && (state.expiresAt || 0) > Date.now() + 30000)
        return state.token
      if (await refresh()) return state.token!
      throw new Error('Your session has expired. Please log in again.')
    },
  }
}
